import React, { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Building2, LayoutDashboard, Users, CalendarCheck2, PalmtreeIcon, PlaneTakeoff, ClipboardCheck, Menu, X, LogOut, User, FileText, Settings, UserCircle, Stethoscope, Bell } from "lucide-react";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";

const NAV = {
  admin: [
    { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "nav-dashboard" },
    { to: "/notifications", label: "Notifications", icon: Bell, testid: "nav-notifications" },
    { to: "/employees", label: "Employees", icon: Users, testid: "nav-employees" },
    { to: "/attendance/import", label: "Import Attendance", icon: ClipboardCheck, testid: "nav-attendance-import" },
    { to: "/holidays", label: "Holidays", icon: PalmtreeIcon, testid: "nav-holidays" },
    { to: "/consultants", label: "Doctors", icon: Stethoscope, testid: "nav-consultants" },
    { to: "/staffing", label: "Staffing", icon: Users, testid: "nav-staffing" },
    { to: "/company", label: "Company", icon: Settings, testid: "nav-company" },
  ],
  manager: [
    { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "nav-dashboard" },
    { to: "/notifications", label: "Notifications", icon: Bell, testid: "nav-notifications" },
    { to: "/employees", label: "My Team", icon: Users, testid: "nav-employees" },
    { to: "/attendance", label: "Attendance", icon: CalendarCheck2, testid: "nav-attendance" },
    { to: "/leaves", label: "Leaves", icon: PlaneTakeoff, testid: "nav-leaves" },
    { to: "/regularizations", label: "Regularizations", icon: ClipboardCheck, testid: "nav-regs" },
    { to: "/payslips", label: "Payslips", icon: FileText, testid: "nav-payslips" },
    { to: "/holidays", label: "Holidays", icon: PalmtreeIcon, testid: "nav-holidays" },
  ],
  employee: [
    { to: "/", label: "Home", icon: LayoutDashboard, end: true, testid: "nav-dashboard" },
    { to: "/notifications", label: "Notifications", icon: Bell, testid: "nav-notifications" },
    { to: "/attendance", label: "Attendance", icon: CalendarCheck2, testid: "nav-attendance" },
    { to: "/leaves", label: "Leaves", icon: PlaneTakeoff, testid: "nav-leaves" },
    { to: "/payslips", label: "Payslips", icon: FileText, testid: "nav-payslips" },
    { to: "/me", label: "My profile", icon: UserCircle, testid: "nav-my-profile" },
    { to: "/holidays", label: "Holidays", icon: PalmtreeIcon, testid: "nav-holidays" },
  ],
};

function initials(name = "") {
  return name.split(" ").map((s) => s[0]).filter(Boolean).slice(0, 2).join("").toUpperCase() || "U";
}

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const navKey =
    user?.role === "admin" ? "admin" : user?.manages ? "manager" : user?.role;
  const items = NAV[navKey] || [];

  // Badge on the Notifications link. Purely a convenience, so a failure here
  // must never stop the rest of the shell rendering.
  const [pending, setPending] = useState(0);
  useEffect(() => {
    let alive = true;
    const tick = async () => {
      try {
        const { data } = await api.get("/notifications");
        if (alive) setPending(data?.count || 0);
      } catch (e) {
        /* ignored on purpose */
      }
    };
    tick();
    const h = setInterval(tick, 60000);
    return () => {
      alive = false;
      clearInterval(h);
    };
  }, [user?.id]);

  const handleLogout = async () => {
    await logout();
    nav("/login");
  };

  return (
    <div className="min-h-screen bg-zinc-50 text-zinc-900">
      {/* Sidebar (desktop) */}
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-[260px] flex-col border-r border-zinc-200 bg-white">
        <div className="h-16 flex items-center gap-2 px-6 border-b border-zinc-200">
          <div className="h-8 w-8 rounded-md bg-emerald-600 grid place-items-center">
            <Building2 className="h-4 w-4 text-white" strokeWidth={1.75} />
          </div>
          <span className="font-display text-lg font-bold tracking-tight">Arborvitae HR</span>
        </div>
        <nav className="flex-1 p-3 space-y-0.5" data-testid="sidebar-nav">
          {items.map((it) => (
            <NavLink
              key={it.to} to={it.to} end={it.end}
              className={({ isActive }) =>
                `group flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                  isActive
                    ? "bg-emerald-50 text-emerald-700 font-medium"
                    : "text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900"
                }`
              }
              data-testid={it.testid}
            >
              <it.icon className="h-[18px] w-[18px]" strokeWidth={1.6} />
              <span>{it.label}</span>
              {it.to === "/notifications" && pending > 0 && (
                <span className="ml-auto min-w-[18px] h-[18px] px-1 rounded-full bg-emerald-600 text-white text-[11px] leading-[18px] text-center">
                  {pending}
                </span>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-zinc-200">
          <UserBadge user={user} onLogout={handleLogout} />
        </div>
      </aside>

      {/* Mobile top bar */}
      <header className="lg:hidden sticky top-0 z-30 h-14 border-b border-zinc-200 bg-white/85 backdrop-blur-md flex items-center justify-between px-4">
        <div className="flex items-center gap-2">
          <div className="h-7 w-7 rounded-md bg-emerald-600 grid place-items-center">
            <Building2 className="h-4 w-4 text-white" strokeWidth={1.75} />
          </div>
          <span className="font-display text-base font-bold">Arborvitae HR</span>
        </div>
        <button onClick={() => setOpen((v) => !v)} className="p-2 rounded-md hover:bg-zinc-100" data-testid="mobile-menu-btn">
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </header>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-40 bg-black/30" onClick={() => setOpen(false)}>
          <div className="absolute top-14 left-0 right-0 bg-white border-b border-zinc-200 p-3" onClick={(e) => e.stopPropagation()}>
            {items.map((it) => (
              <NavLink
                key={it.to} to={it.to} end={it.end}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm ${
                    isActive ? "bg-emerald-50 text-emerald-700 font-medium" : "text-zinc-700"
                  }`
                }
              >
                <it.icon className="h-[18px] w-[18px]" />
                <span>{it.label}</span>
                {it.to === "/notifications" && pending > 0 && (
                  <span className="ml-auto min-w-[18px] h-[18px] px-1 rounded-full bg-emerald-600 text-white text-[11px] leading-[18px] text-center">
                    {pending}
                  </span>
                )}
              </NavLink>
            ))}
            <div className="border-t border-zinc-200 mt-2 pt-2">
              <UserBadge user={user} onLogout={handleLogout} />
            </div>
          </div>
        </div>
      )}

      {/* Main */}
      <main className="lg:pl-[260px]">
        <div className="max-w-[1400px] mx-auto p-4 sm:p-6 lg:p-8 animate-fade-in-up">
          {children}
        </div>
        {/* Mobile bottom nav */}
        <div className="lg:hidden sticky bottom-0 z-30 bg-white/95 backdrop-blur-md border-t border-zinc-200 grid grid-cols-4">
          {items.slice(0, 4).map((it) => (
            <NavLink
              key={it.to} to={it.to} end={it.end}
              className={({ isActive }) =>
                `flex flex-col items-center gap-1 py-2 text-[11px] ${isActive ? "text-emerald-700" : "text-zinc-500"}`
              }
            >
              <it.icon className="h-5 w-5" />
              <span>{it.label}</span>
              {it.to === "/notifications" && pending > 0 && (
                <span className="ml-auto min-w-[18px] h-[18px] px-1 rounded-full bg-emerald-600 text-white text-[11px] leading-[18px] text-center">
                  {pending}
                </span>
              )}
            </NavLink>
          ))}
        </div>
      </main>
    </div>
  );
}

function UserBadge({ user, onLogout }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="w-full flex items-center gap-3 p-2 rounded-md hover:bg-zinc-50 text-left" data-testid="user-menu-btn">
          <Avatar className="h-8 w-8">
            <AvatarFallback className="bg-emerald-100 text-emerald-700 text-xs font-semibold">{initials(user?.name)}</AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium truncate">{user?.name}</div>
            <div className="text-[11px] text-zinc-500 truncate capitalize">{user?.role}</div>
          </div>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="text-xs text-zinc-500">{user?.email}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={onLogout} className="text-red-600 focus:text-red-700" data-testid="logout-btn">
          <LogOut className="h-4 w-4 mr-2" /> Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
